import { Datepicker } from '@/components/datepicker';
import { httpService, langService, routerService, toastService } from '@/core';
import { authService } from '@/models/auth';
import { moduleService } from '@/models/module';
import { translationUrl } from '@/api/sources';
import { getMeta, onDomReady } from '@/utils/browser';
import PrettyConsole from '@/utils/pretty-console';
import * as Sentry from '@sentry/vue';
import flatMap from 'lodash/flatMap';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { useUpdateStore } from '@/stores/update';
import VueTippy from 'vue-tippy';
import '../resources/sass/style.scss';
import app from './app';
import Lang from '@/core/plugins/lang';
import { BrowserTracing } from '@sentry/tracing';
import { globalRoutes, protectedRoutes } from '@/routes';
import { useSidebarStore } from '@/stores/sidebar';
import { useAuthStore } from '@/stores/auth';
import { featureFlagService } from '@/models/feature-flag';

const Vue = createApp(app);
const pinia = createPinia();

Vue.use(pinia);

Vue.use(VueTippy, {
  directive: 'tippy',
  component: 's-tippy',
});

if (process.env.SENTRY_ENVIRONMENT !== 'local') {
  Sentry.init({
    Vue,
    dsn: process.env.SENTRY_VUE_DSN,
    environment: process.env.SENTRY_ENVIRONMENT,
    release: getMeta('app_version'),
    // integrations: [new Integrations.Vue({ Vue, attachProps: false, logErrors: true })],
    integrations: [new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(routerService._router),
      tracePropagationTargets: [/saskaita123\.lt/u],
    })],
    ignoreErrors: [
      // Network errors
      'timeout of 0ms exceeded',
      'Network Error',
      'ECONNABORTED',
      // Firefox, some user extension is attempting to modify navigator.userAgent
      'can\'t redefine non-configurable property "userAgent"',
      // Firefox bug + from some extension/library
      'ResizeObserver loop completed with undelivered notifications.',
      // Chrome, caused by Grammarly extension
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured with keys: [object has no keys]',
      // Webpack chunk load error
      /Loading chunk/u,
      // Crisp error
      /decodeAudioData/u,
      // Just ignore http errors in case some slip past
      /Request failed with status code/u,
      // Vue router
      /Redirected when going from/u,
      // Firefox generic exception, usually race condition on object removed? (cause: tinymce)
      /NS_ERROR_UNEXPECTED/u,
      // Some malformed urls
      /SecurityError: Failed to execute 'replaceState' on 'History'/u,
    ],
  });

  Sentry.attachErrorHandler(Vue, { logErrors: true, attachProps: false });
}

let routeAfterLogin = null;

httpService.setCommonHeaders({
  'x-CSRF-TOKEN': getMeta('csrf-token'),
  'X-Requested-With': 'XMLHttpRequest',
  Accept: 'application/json',
});

httpService
  .addResponseInterceptor(
    response => {
      if (response.headers['x-server-version']) {
        if (useUpdateStore().version === null) {
          useUpdateStore().setVersion(response.headers['x-server-version']);
        } else if (useUpdateStore().version !== response.headers['x-server-version']) {
          useUpdateStore().setMismatch(true);
        }
      }

      if (response.headers['x-refresh-version']) {
        if (useUpdateStore().refresh_version === null) {
          useUpdateStore().setRefreshVersion(response.headers['x-refresh-version']);
        } else if (useUpdateStore().refresh_version !== response.headers['x-refresh-version']) {
          useUpdateStore().setRefreshVersionMismatch(true);
        }
      }

      return response;
    },
    error => Promise.reject(error),
  );

httpService
  .addResponseInterceptor(
    response => response,
    async error => {
      const status = error.response?.status;

      if (status === 401 && useAuthStore().isLoggedIn) {
        const to = routerService.current;
        routeAfterLogin = {
          name: to.name,
          params: to.params,
          query: to.query,
        };

        await useAuthStore().clear();
        window.location.reload();
      } else if (status === 403) {
        toastService.error({ text: toastService.getMessageFromResponse(error) });
      } else if (status === 419) {
        window.location.reload();
      }

      return Promise.reject(error);
    },
  );

httpService.addResponseInterceptor(async response => {
  const twoFactorChallenge = response.data?.two_factor;
  const newDeviceChallenge = response.data?.new_device;
  const contactEmail = response.data?.contact_email;

  if (twoFactorChallenge) {
    useAuthStore().setContactEmail(contactEmail);

    if (newDeviceChallenge) {
      useAuthStore().setNewDeviceChallengeModalVisible(true);
    } else {
      useAuthStore().setTwoFactorChallengeModalVisible(true);
    }

    const isModalVisible = () => newDeviceChallenge ? useAuthStore().newDeviceChallengeModalVisible : useAuthStore().twoFactorChallengeModalVisible;

    await new Promise(resolve => {
      (function waitForAuth() {
        if (useAuthStore().twoFactorChallengeCompleted || !isModalVisible()) {
          if (newDeviceChallenge) {
            useAuthStore().setNewDeviceChallengeModalVisible(false);
          } else {
            useAuthStore().setTwoFactorChallengeModalVisible(false);
          }

          response.data.two_factor_challenge_completed = true;
          resolve();
        } else {
          setTimeout(waitForAuth, 100);
        }
      })();
    });
  }

  return response;
}, error => Promise.reject(error));

routerService.onError((error) => {
  // Webpack chunk missing error
  if (error.name === 'ChunkLoadError') {
    window.location.reload();
    return;
  }

  throw error;
});

// Add global auth route guard
routerService.beforeEach((routeTo, routeFrom, next) => {
  const authRequired = routeTo.matched.find(route => route.meta.auth && route.meta.auth.required);
  const loggedIn = useAuthStore().isLoggedIn;

  if (authRequired) {
    if (loggedIn) {
      if (routeAfterLogin === null) {
        next();
      } else {
        const toRoute = routeAfterLogin;
        routeAfterLogin = null;
        next(toRoute);
      }

      return;
    }

    let destination = null;

    if (routeTo.meta.auth && routeTo.meta.auth.redirectGuest) {
      destination = routeTo.meta.auth.redirectGuest;
    } else if (authRequired.meta.auth.redirectGuest) {
      destination = authRequired.meta.auth.redirectGuest;
    }

    if (destination) {
      if (process.env.NODE_ENV !== 'production') {
        PrettyConsole.debug('beforeEach', 'Unauthenticated, redirecting to guest route.', destination);
      }

      next(destination);
      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      PrettyConsole.debug('beforeEach', 'Unauthenticated but no destination found. Preventing navigation.');
    }

    return;
  }

  const redirectTo = routeTo.matched.find(route => route.meta?.auth?.redirectGuest);
  if (!authRequired && loggedIn && redirectTo && routeTo.name !== redirectTo.meta.auth.redirectGuest.name) {
    next(redirectTo.meta.auth.redirectGuest);
    return;
  }

  next();
});

routerService.afterEach(() => {
  if (window.innerWidth < 768) {
    useSidebarStore().updateMobileOpen(false);
  }
});

routerService.beforeEach((routeTo, from, next) => {
  const matched = flatMap(routeTo.matched.filter(route => Boolean(route.meta.middleware)), route => route.meta.middleware);

  if (matched && matched.length) {
    let allow = true;

    for (const mw of matched) {
      const result = mw(routeTo, from);

      if (result !== undefined) {
        allow = result;
      }

      if (allow !== true) {
        if (allow === false) {
          if (from.name === null) {
            next({ name: 'empty' });
            return;
          }

          next(false);
        } else {
          next(allow);
        }

        return;
      }
    }
  }

  next();
});

routerService.addGlobalRoute(globalRoutes());
routerService.addProtectedRoute(protectedRoutes());

Vue.component('SButton', require('./components/button').Button);
Vue.component('SError', require('./components/form').Error);
Vue.component('SForm', require('./components/form').Form);
Vue.component('SFormGroup', require('./components/form').FormGroup);
Vue.component('SPopper', require('./components/floating').Floating);
Vue.component('SLoader', require('./components/loader').Loader);
Vue.component('SPagination', require('./components/pagination').Pagination);
Vue.component('SSelect', require('./components/select').Select);
Vue.component('STable', require('./components/table').Table);
Vue.component('SCheckbox', require('./components/checkbox').Checkbox);
Vue.component('SModal', require('./components/modal').Modal);
Vue.component('SDatepicker', Datepicker);
Vue.component('SNumericInput', require('./components/numeric-input').NumericInput);
Vue.component('SNumericButton', require('./components/number-button').NumberButton);
Vue.component('SRichText', require('./components/editor').Tinymce);
Vue.component('STextarea', require('./components/textarea').TextArea);
Vue.component('SSwitch', require('./components/switch').Switch);
Vue.component('SUiBlock', require('./components/ui-block').UiBlock);
Vue.component('SToggleButton', require('./components/toggle-button').ToggleButton);
Vue.component('SFileSelect', require('./components/file-select').FileSelect);

Vue.use(vue => {
  Object.defineProperty(vue.config.globalProperties, '$user', {
    get: () => useAuthStore().user,
  });
});

Vue.use(vue => {
  Object.defineProperty(vue.config.globalProperties, '$company', {
    get: () => useAuthStore().company,
  });
});

Vue.use(vue => {
  Object.defineProperty(vue.config.globalProperties, '$auth', {
    get: () => authService,
  });
});

Vue.use(vue => {
  Object.defineProperty(vue.config.globalProperties, '$module', {
    get: () => moduleService,
  });
});

Vue.use(vue => {
  Object.defineProperty(vue.config.globalProperties, '$currencySymbol', {
    get: () => useAuthStore()?.company?.currency_symbol,
  });
});

Vue.use(vue => {
  Object.defineProperty(vue.config.globalProperties, '$feature', {
    get: () => featureFlagService,
  });
});

useAuthStore()
  .boot()
  .then(async () => {
    const response = await httpService.get(translationUrl());
    await langService.load(response.data.data.locale, response.data.data.translations, response.data.data.country);

    // Very rare occasions but happens where js executes before the DOM is ready,
    // so the "body" is not yet available for use in layer service.
    onDomReady(() => {
      if (process.env.SENTRY_ENVIRONMENT !== 'local') {
        Sentry.setUser({ email: useAuthStore().user?.profile?.email });
      }

      Vue.use(routerService._router);
      Vue.use(Lang);
      Vue.mount('#app');
    });
  });
